import { create } from "zustand";
import { track } from "../analytics";
import type { RecoverFinishRequest, RegisterRequest, User } from "@gooner/shared";
import { clearUserToken, getUserToken, testBotProps, userApi } from "./api";
import { identifyUser, registerSuper, resetIdentity } from "../analytics";

/**
 * Everything analytics needs to know when who-is-signed-in changes. The ONLY place in the
 * app that identifies a person to PostHog.
 *
 * Two things, and the order matters. `signed_in` is the super property the funnels keep
 * needing (do accounts pair more screens? do anonymous visitors ever collect?) — it is
 * registered FIRST because signing out then resets the SDK, and reset() replays the super
 * properties held at that moment. Register after, and the replay would put
 * `signed_in: true` straight back onto a signed-out browser.
 *
 * Then the identity: the account uuid, with the username as a person property. Signing out
 * resets instead, minting a fresh anonymous id — a shared browser must not go on crediting
 * the next visitor's browsing to whoever just left.
 *
 * Called wherever the answer changes, which is exactly the seven places below.
 *
 * `premium` rides as a person property beside the name: whether the account is a member
 * as of this stamp. It is what lets any client event — the cap nudge, the wall doors, a
 * session's Faps — be broken down by tier in PostHog without every event carrying it. A
 * boolean, never the expiry: the date would let a dashboard read a person's billing.
 */
const stampIdentity = (user: User | null, testBot?: { is_test_bot: true }) => {
  registerSuper({ signed_in: user !== null });
  // `testBot` only from a sign-in/sign-up the server flagged (testBotProps): sets the
  // person property is_test_bot once; nothing ever unsets it, nothing else sends it.
  if (user) identifyUser(user.id, { username: user.username, premium: (user.goldUntil ?? 0) > Date.now(), ...testBot });
  else resetIdentity();
};

/**
 * App-wide user session. Optional: anonymous users have `user === null` and can
 * browse everything, they just can't save. `ready` flips true once the initial
 * token check resolves (so UI can avoid flashing a signed-out state).
 */
interface SessionState {
  user: User | null;
  ready: boolean;
  init: () => Promise<void>;
  /** `gooncha` is the one-shot pass token, required while the robot check is enabled. */
  login: (username: string, password: string, gooncha?: string) => Promise<void>;
  /** One object — registration carries seven fields now. See userApi.register. */
  register: (body: RegisterRequest) => Promise<void>;
  /** Password recovery's last step — signs in on success, like login. */
  recover: (body: RecoverFinishRequest) => Promise<void>;
  logout: () => Promise<void>;
  /** Erase the account (password required), then sign out locally. Throws on refusal. */
  deleteAccount: (password: string) => Promise<void>;
  /** Replace the cached user with a freshly fetched one. For the post-checkout page,
   *  which polls until the webhook's grant shows up — the entitlement changes on the
   *  SERVER while this tab sits still, so nothing else here would ever notice. */
  refresh: (user: User) => void;
}

export const useSession = create<SessionState>((set) => ({
  user: null,
  ready: false,

  refresh: (user) => {
    stampIdentity(user);
    set({ user });
  },

  init: async () => {
    if (!getUserToken()) {
      stampIdentity(null);
      set({ ready: true });
      return;
    }
    try {
      const { user } = await userApi.me();
      stampIdentity(user);
      set({ user, ready: true });
    } catch {
      clearUserToken();
      stampIdentity(null);
      set({ user: null, ready: true });
    }
  },

  login: async (username, password, gooncha) => {
    const res = await userApi.login(username, password, gooncha);
    const { user } = res;
    const testBot = testBotProps(res);
    stampIdentity(user, testBot);
    set({ user });
    // Counted HERE rather than in the modal, so every entry point (the remote, a gated
    // fap, a collection, the account menu) is one event — and one that now lands on the
    // person stampIdentity just established, rather than on an anonymous id. The only
    // send: lib/api.ts no longer sends its own copy.
    track("signed_in", testBot);
  },

  register: async (body) => {
    const res = await userApi.register(body);
    const { user } = res;
    const testBot = testBotProps(res);
    stampIdentity(user, testBot);
    set({ user });
    // The conversion, and its ONLY send (lib/api.ts used to send a second, pre-identify
    // copy). On success only; nothing about the account in the props beyond is_test_bot
    // for flagged QA traffic. `$pathname` says which door they came through (the landing,
    // the remote, the editor's save prompt).
    track("account_created", testBot);
  },

  // No signed_in: recovery's event is the server's password_recovered. Deliberate, and
  // pinned by tools/checks/auth-event-dedupe.mts.
  recover: async (body) => {
    const res = await userApi.recoverFinish(body);
    const { user } = res;
    stampIdentity(user, testBotProps(res));
    set({ user });
  },

  logout: async () => {
    await userApi.logout().catch(() => undefined);
    stampIdentity(null);
    set({ user: null });
    track("signed_out");
  },

  // No browser event: the server's account_deleted is the truth and is personless. The
  // identity reset comes after the request so a refusal leaves the session as it was.
  deleteAccount: async (password) => {
    await userApi.deleteAccount({ password });
    stampIdentity(null);
    set({ user: null });
  },
}));
